export interface IngredientTagMetadata {
  tag: string;
  nameEn: string;
  /** null where the game/site has no composite art for this slot — the UI renders no thumbnail
   * rather than a broken one. Do NOT substitute one member's icon: a slot that accepts any bird
   * is not a picture of one bird. */
  imageUrl: string | null;
  defaultPrice: number | null;
}

export const INGREDIENT_TAG_METADATA: Record<string, IngredientTagMetadata> = {
  ANY_FISH: { tag: "ANY_FISH", nameEn: "Any Fish", imageUrl: "/icons/ingredient/any-fish.webp", defaultPrice: 50 },
  ANY_INSECT: { tag: "ANY_INSECT", nameEn: "Any Insect", imageUrl: null, defaultPrice: 30 },
  ANY_BIRD: { tag: "ANY_BIRD", nameEn: "Any Bird", imageUrl: null, defaultPrice: 40 },
  ANY_FORAGEABLE: { tag: "ANY_FORAGEABLE", nameEn: "Any Forageable", imageUrl: null, defaultPrice: 20 },
  ANY_CROP: { tag: "ANY_CROP", nameEn: "Any Crop", imageUrl: null, defaultPrice: 30 },
  ANY_FRUIT: { tag: "ANY_FRUIT", nameEn: "Any Fruit", imageUrl: "/icons/ingredient/any-fruit.webp", defaultPrice: 25 },
  ANY_DREAMLIGHT_WILD_GREEN: {
    tag: "ANY_DREAMLIGHT_WILD_GREEN",
    nameEn: "Any Dreamlight Wild Green",
    imageUrl: "/icons/dish/wild-vegetable-celtuce-taco.webp",
    defaultPrice: null,
  },
  ANY_DREAMLIGHT_BLACK_TEA: {
    tag: "ANY_DREAMLIGHT_BLACK_TEA",
    nameEn: "Any Dreamlight Black Tea",
    imageUrl: "/icons/dish/springday-fruit-black-tea.webp",
    defaultPrice: null,
  },
  ANY_DREAMLIGHT_CELTUCE_TACO: {
    tag: "ANY_DREAMLIGHT_CELTUCE_TACO",
    nameEn: "Any Dreamlight Celtuce Taco",
    imageUrl: "/icons/dish/wild-vegetable-celtuce-taco.webp",
    defaultPrice: null,
  },
  ANY_VEGETABLE: {
    tag: "ANY_VEGETABLE",
    nameEn: "Any Vegetable",
    imageUrl: "/icons/ingredient/any-vegetable.webp",
    defaultPrice: 20,
  },
  ANY_HERB: { tag: "ANY_HERB", nameEn: "Any Herb", imageUrl: null, defaultPrice: 40 },
  ANY_SPICE: { tag: "ANY_SPICE", nameEn: "Any Spice", imageUrl: null, defaultPrice: 35 },
  ANY_MUSHROOM: {
    tag: "ANY_MUSHROOM",
    nameEn: "Any Mushroom",
    imageUrl: "/icons/ingredient/any-mushroom.webp",
    defaultPrice: 16,
  },
  ANY_JAM_FRUIT: {
    tag: "ANY_JAM_FRUIT",
    nameEn: "Any Jam Fruit",
    imageUrl: "/icons/ingredient/any-jam-fruit.webp",
    defaultPrice: 30,
  },
  ANY_JAM: { tag: "ANY_JAM", nameEn: "Any Jam", imageUrl: null, defaultPrice: 80 },
  ANY_RAINBOW_SUGAR: {
    tag: "ANY_RAINBOW_SUGAR",
    nameEn: "Any Rainbow Sugar",
    imageUrl: "/icons/ingredient/any-rainbow-sugar.webp",
    defaultPrice: 50,
  },
  ANY_CRAYFISH: {
    tag: "ANY_CRAYFISH",
    nameEn: "Any Crayfish",
    imageUrl: "/icons/ingredient/any-crayfish.webp",
    defaultPrice: 100,
  },
  ANY_FAILED_DISH: {
    tag: "ANY_FAILED_DISH",
    nameEn: "Any Failed Dish",
    imageUrl: "/icons/ingredient/any-failed-dish.webp",
    defaultPrice: 0,
  },
};

export function getIngredientTagName(tag: string | null | undefined): string {
  if (!tag) return "Unknown";
  return INGREDIENT_TAG_METADATA[tag]?.nameEn ?? tag;
}

export function getIngredientTagImageUrl(tag: string | null | undefined): string | null {
  if (!tag) return null;
  return INGREDIENT_TAG_METADATA[tag]?.imageUrl ?? null;
}

export function getIngredientTagPrice(tag: string | null | undefined): number | null {
  if (!tag) return null;
  return INGREDIENT_TAG_METADATA[tag]?.defaultPrice ?? null;
}

/**
 * Tags that map cleanly 1:1 onto a full ItemKind — used as a fallback "expand" list when a
 * recipe has no Heartodex-confirmed Mix & Match options (see RecipeIngredientRef.mixOptionItemIds).
 * Deliberately NOT exhaustive: tags like ANY_FRUIT/ANY_VEGETABLE/ANY_MUSHROOM are subsets of a
 * kind, not the whole kind, and research confirmed Mix & Match lists are curated per recipe —
 * so we only offer this fallback where "every item of this kind" is actually a safe superset,
 * and the UI must label it as "possible" rather than "confirmed".
 */
export const TAG_FALLBACK_KIND: Record<string, string> = {
  ANY_FISH: "fish",
  ANY_INSECT: "insect",
  ANY_BIRD: "bird",
  ANY_FORAGEABLE: "forageable",
  ANY_CROP: "crop",
};
